export const DEFAULT_WAIT_MS = 4000;

export const targetSelector = (target: string) => `[data-tour="${target}"]`;

/** In the DOM, laid out, and not hidden from people or assistive tech. */
export function isTargetVisible(element: Element): boolean {
  if (!element.isConnected) return false;
  if (element.closest('[hidden], [aria-hidden="true"], [inert]')) return false;
  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

export function findTarget(target: string, root: ParentNode = document): HTMLElement | null {
  for (const element of root.querySelectorAll<HTMLElement>(targetSelector(target))) {
    if (isTargetVisible(element)) return element;
  }
  return null;
}

export interface FoundTarget {
  element: HTMLElement;
  target: string;
}

/** First listed target that is visible right now. */
export function findFirstTarget(targets: readonly string[]): FoundTarget | null {
  for (const target of targets) {
    const element = findTarget(target);
    if (element) return { element, target };
  }
  return null;
}

export interface WaitOptions {
  timeoutMs?: number;
  signal?: AbortSignal;
  /** Extra condition, e.g. the router has finished loading. */
  isReady?: () => boolean;
}

/**
 * Resolves with the first target that becomes visible, or null on timeout or abort. Watches DOM
 * mutations and polls once per frame, because layout (a sheet sliding in) changes visibility
 * without a mutation.
 */
export function waitForTarget(
  targets: readonly string[],
  { timeoutMs = DEFAULT_WAIT_MS, signal, isReady = () => true }: WaitOptions = {},
): Promise<FoundTarget | null> {
  return new Promise((resolve) => {
    if (signal?.aborted) {
      resolve(null);
      return;
    }
    const check = () => (isReady() ? findFirstTarget(targets) : null);
    const immediate = check();
    if (immediate) {
      resolve(immediate);
      return;
    }
    let frame = 0;
    let settled = false;
    const finish = (value: FoundTarget | null) => {
      if (settled) return;
      settled = true;
      observer.disconnect();
      cancelAnimationFrame(frame);
      window.clearTimeout(timer);
      signal?.removeEventListener('abort', onAbort);
      resolve(value);
    };
    const tick = () => {
      const found = check();
      if (found) finish(found);
      else frame = requestAnimationFrame(tick);
    };
    const onAbort = () => finish(null);
    const observer = new MutationObserver(() => {
      const found = check();
      if (found) finish(found);
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-tour', 'hidden', 'aria-hidden', 'style', 'class', 'data-state'],
    });
    const timer = window.setTimeout(() => finish(null), timeoutMs);
    signal?.addEventListener('abort', onAbort);
    frame = requestAnimationFrame(tick);
  });
}
